import { createContext, useState, useEffect, useContext } from "react";
import { checkAuth, logout } from "@service/authService";
import { getFixedKey, encryptMessage, decryptMessage } from "@utils/crypto.ts";
import { hasPermission, setPermissionsList } from "./Permissions";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [info, setInfo] = useState({});
  const [key, setKey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initKeyAndSession = async () => {
      if (!process.env.REACT_APP_BB_SECRET) {
        console.error(
          "REACT_APP_BB_SECRET is not set. Crypto key cannot be generated."
        );
        return;
      }
      try {
        const k = await getFixedKey(process.env.REACT_APP_BB_SECRET);
        setKey(k);

        const cryptedInfo = JSON.parse(sessionStorage.getItem("@b&b:info"));
        if (cryptedInfo) {
          const getInfo = await decryptMessage(
            cryptedInfo.ciphertext,
            k,
            cryptedInfo.iv
          );
          if (getInfo) {
            setInfo(getInfo);
            setPermissionsList(JSON.parse(getInfo).permissions || []);
          }
        }
      } catch (err) {
        console.error("Failed to generate crypto key or decrypt info:", err);
      }
    };

    const verifyUser = async () => {
      const data = await checkAuth();
      setUser(data);
      setLoading(false);
    };

    initKeyAndSession();
    verifyUser();

    const interval = setInterval(() => {
      verifyUser();
    }, 600000);

    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    setUser(null);

    // Preserve language preference before clearing storage
    const savedLanguage = localStorage.getItem("appLanguage");

    await logout();
    window.localStorage.clear();
    window.sessionStorage.clear();

    // Restore language preference after clearing
    if (savedLanguage) {
      window.localStorage.setItem("appLanguage", savedLanguage);
    }

    window.location.href = "/react/landing";
  };

  const handleInfo = async (info) => {
    if (!key) return;
    setPermissionsList(info.permissions || []);
    const { ciphertext, iv } = await encryptMessage(JSON.stringify(info), key);
    sessionStorage.setItem("@b&b:info", JSON.stringify({ ciphertext, iv }));
    setInfo(info);
  };

  const getInfo = async () => {
    if (!key) return {};
    const cryptedInfo = JSON.parse(sessionStorage.getItem("@b&b:info"));
    if (cryptedInfo) {
      const getInfo = await decryptMessage(
        cryptedInfo.ciphertext,
        key,
        cryptedInfo.iv
      );
      if (getInfo) {
        setPermissionsList(JSON.parse(getInfo).permissions || []);
        return JSON.parse(getInfo);
      }
    }
    return {};
  };

  const getIsPayrollAdmin = async () => {
    if (!key) return false;

    const cryptedInfo = JSON.parse(sessionStorage.getItem("@b&b:info"));

    if (cryptedInfo) {
      const getInfo = await decryptMessage(
        cryptedInfo.ciphertext,
        key,
        cryptedInfo.iv
      );
      const getInfoParsed = JSON.parse(getInfo);
      if (
        getInfo &&
        getInfoParsed.roles &&
        Array.isArray(getInfoParsed.roles)
      ) {
        return getInfoParsed.roles.some(
          (role) => role.name === "Payroll Admin"
        );
      }
    }
    return false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        info,
        getInfo,
        setUser,
        handleInfo,
        handleLogout,
        hasPermission,
        getIsPayrollAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return context;
}
